import { ColumnsType } from 'antd/es/table';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { actionTitleMapper, HistoryAction, useGetPaginatedHistoryQuery } from 'entities/History';
import { TableFilterType, useTableFilterContext } from 'features/TableFilter';
import { FC, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableActions } from 'shared/ui/ServerTable/types';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { getClientDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { transformHistoryFilters } from '../utils/transformHistoryFilters';
import { HistoryFilter } from './HistoryFilter';

export const HistoryTable: FC = memo(() => {
  const { t } = useAppTranslation('profile');

  const navigate = useNavigate();

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();
  const { parsedQuery, decode } = useQueryString();

  const externalFilters = decode(parsedQuery) as TableFilterType;

  const goToUserProfile = useCallback(
    (userId?: string): void => {
      if (userId) {
        navigate(`${AppRoutes.PROFILE}/${userId}`);
      }
    },
    [navigate],
  );

  const columns = useMemo<ColumnsType<HistoryAction>>(
    () => [
      {
        title: t('Date'),
        key: 'createdAt',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => getClientDateWithMinuteTimeFormat(record.createdAt),
      },
      {
        title: t('Customer'),
        key: 'user',
        render: (_, record) => (
          <div className="flex flex-col">
            <div
              className="text-accent font-semibold underline cursor-pointer"
              onClick={() => {
                goToUserProfile(record.actionData?.userId);
              }}
            >
              {record.actionData?.userName}
            </div>
          </div>
        ),
      },
      {
        title: t('Subject'),
        key: 'subject',
        render: (_, record) => {
          const { subject } = actionTitleMapper(record.actionName, record.actionData, t);

          return subject;
        },
      },
      {
        title: t('Action'),
        key: 'action',
        render: (_, record) => {
          const { action } = actionTitleMapper(record.actionName, record.actionData, t);

          return action;
        },
      },
      {
        title: t('Object'),
        key: 'object',
        render: (_, record) => {
          const { object } = actionTitleMapper(record.actionName, record.actionData, t);

          return object;
        },
      },
    ],
    [goToUserProfile, t],
  );

  const tableActions: Array<ServerTableActions<HistoryAction>> = useMemo(
    () => [
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [setFiltersOpened, t],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('History')}</div>
      <ServerTable
        columns={columns}
        fetchData={useGetPaginatedHistoryQuery}
        rowKey="actionId"
        search
        tableActionsPosition="prev"
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        tableActions={tableActions}
        defaultFilters={transformHistoryFilters(appliedFilters)}
        defaultSearch={externalFilters.search?.value}
      />
      <HistoryFilter />
    </div>
  );
});
